import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import ForumPostCard, { TAG_COLORS } from "../components/ForumPostCard";
import { Plus, Flame, Clock, TrendingUp } from "lucide-react";



async function fetchPosts({ sort, tag, q, userId }) {
  let query = supabase
    .from("forum_posts")
    .select("*, author:profiles!user_id(id,name,avatar_url,participant_id), forum_post_votes(user_id)")
    .limit(50);

  if (tag)  query = query.contains("tags", [tag]);
  if (sort === "hot")  query = query.order("upvotes", { ascending: false });
  else if (sort === "top") query = query.order("reply_count", { ascending: false });
  else                     query = query.order("created_at", { ascending: false });

  const { data } = await query;
  let posts = (data || []).map((p) => ({
    ...p,
    voted_by_me: (p.forum_post_votes || []).some((v) => v.user_id === userId),
  }));

  // Pinned first
  posts.sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0));

  if (q) {
    const ql = q.toLowerCase();
    posts = posts.filter((p) =>
      p.title.toLowerCase().includes(ql) ||
      p.body.toLowerCase().includes(ql) ||
      (p.tags || []).some((t) => t.includes(ql.replace("#", "")))
    );
  }
  return posts;
}

export default function Forum() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [posts,  setPosts]  = useState([]);
  const [sort,   setSort]   = useState(params.get("sort") || "hot");
  const [tag,    setTag]    = useState(params.get("tag")  || "");
  const [q,      setQ]      = useState(params.get("q")    || "");
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);

  const votedIds = new Set(posts.filter((p) => p.voted_by_me).map((p) => p.id));

  // Fetch all unique tags from forum posts for the sidebar filter
  useEffect(() => {
    supabase.from("forum_posts").select("tags").then(({ data }) => {
      const tagSet = new Set();
      (data || []).forEach((p) => (p.tags || []).forEach((t) => tagSet.add(t)));
      setAllTags([...tagSet].sort());
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchPosts({ sort, tag, q, userId: user?.id })
      .then(setPosts)
      .finally(() => setLoading(false));
    setParams({ ...(q ? { q } : {}), ...(tag ? { tag } : {}), sort });
  }, [sort, tag, q, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onVote = async (post) => {
    if (!user) return;
    const wasVoted = post.voted_by_me;
    const patch = (p) => p.id !== post.id ? p : { ...p, voted_by_me: !wasVoted, upvotes: wasVoted ? p.upvotes - 1 : p.upvotes + 1 };
    setPosts((prev) => prev.map(patch));
    if (wasVoted) {
      await supabase.from("forum_post_votes").delete().eq("post_id", post.id).eq("user_id", user.id);
    } else {
      await supabase.from("forum_post_votes").insert({ post_id: post.id, user_id: user.id });
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid lg:grid-cols-12 gap-6">

        {/* MAIN COLUMN */}
        <div className="lg:col-span-8 space-y-4">
          {/* Header */}
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="font-hand text-marker-red text-2xl rotate-[-1deg]">— drop the hot takes —</div>
              <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight">
                The <span className="highlight-marker">Forum</span>
              </h1>
            </div>
            {user ? (
              <Link to="/forum/new" className="brutal-btn brutal-btn-yellow" data-testid="new-post-btn">
                <Plus size={14} /> New post
              </Link>
            ) : (
              <Link to="/login" className="brutal-btn">Log in to post</Link>
            )}
          </div>

          {/* Search + sort */}
          <div className="brutal-card p-4 flex flex-wrap gap-3 items-center">
            <input
              className="brutal-input flex-1 min-w-[180px] text-sm"
              placeholder="Search discussions…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="forum-search"
            />
            <div className="flex gap-2">
              {[
                { key: "hot",  icon: <Flame size={13} />,        label: "Hot" },
                { key: "new",  icon: <Clock size={13} />,        label: "New" },
                { key: "top",  icon: <TrendingUp size={13} />,   label: "Top" },
              ].map(({ key, icon, label }) => (
                <button key={key} onClick={() => setSort(key)}
                  className={`brutal-btn text-xs flex items-center gap-1 ${sort === key ? "brutal-btn-yellow" : ""}`}
                  data-testid={`sort-${key}`}>
                  {icon} {label}
                </button>
              ))}
            </div>
          </div>

          {/* Posts */}
          {loading ? (
            <div className="brutal-card p-8 font-hand text-2xl">Loading discussions…</div>
          ) : posts.length === 0 ? (
            <div className="brutal-card p-8 font-hand text-2xl">No posts match — be the first.</div>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <ForumPostCard key={p.id} post={p} onVote={onVote} votedIds={votedIds} />
              ))}
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="lg:col-span-4 space-y-5">
          {/* Tag filter */}
          <section className="brutal-card p-5">
            <div className="font-display font-extrabold text-lg uppercase tracking-tight mb-3">Filter by tag</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setTag("")}
                className={`tag-pill ${!tag ? "" : "opacity-60"}`} style={{ background: "#121212", color: "#fff" }}>
                All
              </button>
              {(showAllTags ? allTags : allTags.slice(0, 6)).map((t) => {
                const style = TAG_COLORS[t] || { bg: "#FFD600", color: "#121212" };
                return (
                  <button key={t} onClick={() => setTag(t === tag ? "" : t)} className="tag-pill"
                    style={{ background: tag === t ? style.bg : "#fff", color: tag === t ? style.color : "#121212",
                             border: `2px solid ${style.bg}` }}
                    data-testid={`tag-filter-${t}`}>
                    #{t}
                  </button>
                );
              })}
              {allTags.length > 6 && (
                <button
                  onClick={() => setShowAllTags(!showAllTags)}
                  className="tag-pill font-mono text-[11px]"
                  style={{ background: "transparent", color: "#121212", border: "2px dashed #121212" }}
                >
                  {showAllTags ? "View less ↑" : `+${allTags.length - 6} more ↓`}
                </button>
              )}
            </div>
          </section>

          {/* Community rules */}
          <section className="brutal-card p-5" style={{ background: "#fff", color: "#121212" }}>
            <div className="font-display font-extrabold text-lg uppercase tracking-tight mb-3" style={{ color: "#FFD600", WebkitTextStroke: "1px #121212" }}>
              Forum Rules
            </div>
            <ol className="space-y-2 font-mono text-sm">
              {[
                "Post receipts, not vibes.",
                "Be brutally honest — kindly.",
                "No recruiting, no pitching.",
                "Share what failed, not just what worked.",
                "Lift others before you flex.",
              ].map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className="font-extrabold" style={{ color: "#121212" }}>{i + 1}.</span>
                  <span style={{ color: "#121212" }}>{r}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* CTA */}
          {!user && (
            <section className="brutal-card p-5" style={{ background: "#FFD600" }}>
              <div className="font-hand text-2xl rotate-[-2deg] mb-1">— join in —</div>
              <h3 className="font-display font-extrabold text-xl uppercase tracking-tight">Drop your take</h3>
              <p className="text-sm mt-1">Log in to post, vote, and reply.</p>
              <Link to="/login" className="brutal-btn mt-3 w-full">Log in →</Link>
            </section>
          )}
        </aside>
      </div>
    </main>
  );
}
